import {
    bexHttpClient
} from '../../clients';

/**
 * Studio Agent service — talks to the in-platform agent on bex-server.
 * Endpoints (server mounts under /cm; the /api→/cm rewrite maps these):
 *   POST /cm/agent/chat      -> one agent turn
 *   POST /cm/agent/run-tool  -> execute one tool the user approved (a write)
 * All require a valid JWT (sent via the user-session cookie, withCredentials).
 */

export interface AgentChatMessage {
    role: 'user' | 'assistant';
    content: string;
}

export interface PendingWrite {
    tool: string;
    args: Record<string, unknown>;
    scope: 'create' | 'delete';
}

export interface PendingQuestion {
    id: string;
    question: string;
    detail?: string;
    mode: 'single' | 'multiple';
    options: Array<{
        label: string;
        value: string;
        description?: string;
    }>;
    allowOther?: boolean;
}

export interface AgentChatResponse {
    ok: boolean;
    text?: string;
    finishReason?: string;
    steps?: number;
    pendingWrites?: PendingWrite[];
    pendingQuestions?: PendingQuestion[];
    finish?: Record<string, unknown>;
    usage?: Record<string, unknown>;
    error?: string;
}

export interface AgentRunToolResponse {
    ok: boolean;
    tool?: string;
    result?: unknown;
    error?: string;
}

class StudioAgentService {
    /**
     * Run one agent turn. Reads/validation execute automatically; any write the
     * model proposes comes back in `pendingWrites` for the user to approve.
     */
    async chat(
        organizationId: string,
        messages: AgentChatMessage[],
        model?: string
    ): Promise<AgentChatResponse> {
        const response = await bexHttpClient.post('/agent/chat', {
            organizationId,
            messages,
            model
        });
        // bexHttpClient resolves to the full Axios response; the body is in .data.
        return (response?.data ?? response) as unknown as AgentChatResponse;
    }

    /**
     * Streaming turn. Opens an SSE stream to /cm/agent/stream and invokes the
     * handlers as events arrive: onText(delta) for each text chunk, onDone(evt)
     * with { pendingWrites, steps, finishReason }, onError(message). Uses fetch
     * (not axios) so the response body can be read incrementally; the
     * user-session cookie rides along via credentials:'include'.
     */
    // ---- Sessions (server-saved history) ----

    async listSessions(organizationId: string): Promise<any> {
        const r = await bexHttpClient.get('/agent/sessions', {
 params: {
 organizationId
}
});
        return (r?.data ?? r);
    }

    async getSession(organizationId: string, id: string): Promise<any> {
        const r = await bexHttpClient.get(`/agent/sessions/${id}`, {
 params: {
 organizationId
}
});
        return (r?.data ?? r);
    }

    async createSession(organizationId: string, title?: string): Promise<any> {
        const r = await bexHttpClient.post('/agent/sessions', {
 organizationId,
title
});
        return (r?.data ?? r);
    }

    async renameSession(organizationId: string, id: string, title: string): Promise<any> {
        const r = await bexHttpClient.patch(`/agent/sessions/${id}`, {
 organizationId,
title
});
        return (r?.data ?? r);
    }

    async deleteSession(organizationId: string, id: string): Promise<any> {
        const r = await bexHttpClient.delete(`/agent/sessions/${id}`, {
 params: {
 organizationId
}
});
        return (r?.data ?? r);
    }

    async autotitle(organizationId: string, id: string, model?: string): Promise<any> {
        const r = await bexHttpClient.post(`/agent/sessions/${id}/autotitle`, {
 organizationId,
model
});
        return (r?.data ?? r);
    }

    async streamChat(
        organizationId: string,
        messages: AgentChatMessage[],
        model: string | undefined,
        handlers: {
            onText?: (delta: string) => void;
            onToolInputStart?: (evt: { toolName: string; isWrite: boolean }) => void;
            onToolCall?: (evt: { toolCallId: string; toolName: string; isWrite: boolean; input?: unknown }) => void;
            onToolResult?: (evt: { toolCallId: string; toolName: string; result?: unknown }) => void;
            onDone?: (evt: { pendingWrites?: PendingWrite[]; pendingQuestions?: PendingQuestion[]; finish?: Record<string, unknown>; steps?: number; finishReason?: string; stalled?: boolean }) => void;
            onError?: (message: string) => void;
            onDebugEvent?: (evt: Record<string, unknown>) => void;
            signal?: AbortSignal;
        },
        sessionId?: string,
        attachments?: Array<{ type: 'image' | 'pdf'; name: string; dataUrl: string }>,
        pageContext?: Record<string, unknown>,
        autoApproveWrites?: boolean
    ): Promise<void> {
        const base = process.env.REACT_APP_BEX_SERVER_URL || '';
        const url = `${base}/api/agent/stream`;
        const body = {
 organizationId,
messages,
model,
sessionId,
attachments,
pageContext,
autoApproveWrites
};
        handlers.onDebugEvent?.({
            type: 'request',
            method: 'POST',
            url,
            body: {
                ...body,
                attachments: (attachments || []).map((a) => ({
                    type: a.type,
                    name: a.name,
                    bytes: a.dataUrl?.length || 0,
                })),
            },
        });
        let resp: Response;
        try {
            resp = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
 'Content-Type': 'application/json'
},
                body: JSON.stringify(body),
                signal: handlers.signal,
            });
        } catch (e) {
            // A user-initiated Stop aborts the fetch — that is not an error.
            if ((e as Error)?.name === 'AbortError' || handlers.signal?.aborted) return;
            handlers.onError?.((e as Error)?.message || 'request failed');
            return;
        }
        handlers.onDebugEvent?.({
            type: 'response',
            status: resp.status,
            ok: resp.ok,
            url,
        });
        if (!resp.ok || !resp.body) {
            handlers.onError?.(`HTTP ${resp.status}`);
            return;
        }
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        // The server's 'done' event is the authoritative end of a turn. Once we
        // have it, the turn is complete no matter how the connection then closes.
        let finished = false;
        try {
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const {
 done, value
} = await reader.read();
                if (done) break;
                buf += decoder.decode(value, {
 stream: true
});
                const parts = buf.split('\n\n');
                buf = parts.pop() || '';
                for (const part of parts) {
                    const line = part.split('\n').find((l) => l.startsWith('data:'));
                    if (!line) continue;
                    try {
                        const evt = JSON.parse(line.slice(5).trim());
                        handlers.onDebugEvent?.(evt);
                        if (evt.type === 'text') handlers.onText?.(evt.delta);
                        else if (evt.type === 'tool-input-start') handlers.onToolInputStart?.(evt);
                        else if (evt.type === 'tool-call') handlers.onToolCall?.(evt);
                        else if (evt.type === 'tool-result') handlers.onToolResult?.(evt);
                        else if (evt.type === 'done') { finished = true; handlers.onDone?.(evt); }
                        else if (evt.type === 'error') handlers.onError?.(evt.error);
                    } catch {
                        /* ignore partial/non-JSON keepalive lines */
                    }
                }
            }
        } catch (e) {
            // Stop aborts reader.read() with an AbortError ("BodyStreamBuffer was
            // aborted") — a deliberate stop, not a failure. And an edge/ingress
            // that closes the SSE connection un-cleanly makes reader.read() throw
            // ("Error in input stream") AFTER the turn already completed — the
            // 'done' event proves the turn finished, so that trailing transport
            // error is spurious. Swallow both; surface only a genuine mid-stream
            // read failure that left the turn unfinished.
            if (finished || (e as Error)?.name === 'AbortError' || handlers.signal?.aborted) return;
            handlers.onError?.((e as Error)?.message || 'stream read failed');
        }
    }

    /**
     * Execute ONE tool the user explicitly approved (the confirm path for a
     * write). The server re-checks org membership and keeps the org pinned.
     */
    async runTool(
        organizationId: string,
        tool: string,
        args: Record<string, unknown>
    ): Promise<AgentRunToolResponse> {
        const response = await bexHttpClient.post('/agent/run-tool', {
            organizationId,
            tool,
            args
        });
        return (response?.data ?? response) as unknown as AgentRunToolResponse;
    }
}

const studioAgentService = new StudioAgentService();
export { studioAgentService };
